// Shared helpers for the tree-based MS/GS generation agents. Both `ms-generation-agent.ts` and
// `gs-generation-agent.ts` generate one LLM call per top-level main task, covering its entire subtree,
// so the model can produce coherent solutions across subtasks and distribute a known parent point total
// across subtasks. These helpers walk a task subtree, describe it for the payload, and gate readiness.
// Point distribution itself is left to the agent (guided by the prompt); we only pin already-present
// points via the approved-total lock in the GS agent.

/**
 * Structural view of an exam task node used across the tree-generation helpers. Kept independent of the
 * AdornisEntity `ExamTask` class (whose recursive `subTasks` mapped type otherwise trips the compiler)
 * — callers cast `exam.tasks as NestedExamTask[]`, matching how the agents already treat these nodes.
 */
export interface NestedExamTask {
  _id?: string;
  taskNumber: string;
  description?: string;
  taskType?: string | string[];
  needsAnswer?: boolean;
  boundingBoxes?: any;
  masterSolution?: any;
  gradingScheme?: any;
  subTasks?: NestedExamTask[];
  [key: string]: any;
}

/** Stable per-node key used for progress accounting / in-flight tracking. */
export const getTaskKey = (task: NestedExamTask): string => {
  const rawId = (task as any)?._id;
  if (rawId !== undefined && rawId !== null && String(rawId).trim().length > 0) {
    return String(rawId);
  }
  return `task:${String(task.taskNumber)}`;
};

/** A task is gradable (needs its own MS/GS) unless it was explicitly marked needsAnswer=false. */
export const isGradable = (task: NestedExamTask): boolean => (task as any)?.needsAnswer !== false;

/**
 * Whether a task already carries a master solution — a bounding box counts, not just text.
 *
 * These two predicates decide what the generation agents TARGET (`!has… → generate`), so a task they
 * report as empty gets overwritten. A region the teacher uploaded may be a pure image (a sketch or a
 * drawing) with nothing transcribable, which survives as a bounding box and no `content`: counting only
 * text would treat the teacher's own material as a gap and generate over it. This is the same definition
 * `solution-completeness` uses to decide whether an uploaded solution covers a task, so "complete" and
 * "nothing left to generate" now mean the same thing.
 */
const hasBoxes = (section: any): boolean => {
  const boxes = section?.boundingBoxes ?? section?.doc?.boundingBoxes;
  return Array.isArray(boxes) && boxes.length > 0;
};

export const hasMasterSolutionContent = (task: NestedExamTask): boolean => {
  const ms = (task as any)?.masterSolution;
  return Boolean(typeof ms === 'string' || ms?.content || ms?.doc?.content || hasBoxes(ms));
};

export const hasGradingSchemeContent = (task: NestedExamTask): boolean => {
  const gs = (task as any)?.gradingScheme;
  return Boolean(gs?.criteria || gs?.doc?.criteria || (typeof gs === 'string' && gs.trim().length > 0) || hasBoxes(gs));
};

export interface SubtreeNode {
  node: NestedExamTask;
  /** taskNumber path from the subtree root down to this node, e.g. ["1","a"]. */
  path: string[];
  /** `path` joined with '.', echoed to the model as the task header id (e.g. "1.a"). */
  id: string;
}

/** Pre-order walk (parent before children) of a task subtree, annotating each node with its path/id. */
export const collectSubtree = (root: NestedExamTask): SubtreeNode[] => {
  const out: SubtreeNode[] = [];
  const walk = (node: NestedExamTask, parentPath: string[]) => {
    const path = [...parentPath, String(node?.taskNumber ?? '')];
    out.push({ node, path, id: path.join('.') });
    for (const child of Array.isArray(node?.subTasks) ? node.subTasks : []) walk(child, path);
  };
  walk(root, []);
  return out;
};

/** Unique set of task types present anywhere in the subtree (for multi-taskType prompt injection). */
export const collectSubtreeTaskTypes = (root: NestedExamTask): string[] => {
  const set = new Set<string>();
  for (const { node } of collectSubtree(root)) {
    const tt = (node as any)?.taskType;
    if (typeof tt === 'string' && tt.trim()) set.add(tt.trim());
    else if (Array.isArray(tt)) for (const x of tt) if (typeof x === 'string' && x.trim()) set.add(x.trim());
  }
  return Array.from(set);
};

/** Map from header id (path string) → node, for writing per-node LLM results back onto the subtree. */
export const buildPathMap = (root: NestedExamTask): Map<string, NestedExamTask> => {
  const map = new Map<string, NestedExamTask>();
  for (const { node, id } of collectSubtree(root)) map.set(id, node);
  return map;
};

export const gradableSubtreeNodes = (root: NestedExamTask): SubtreeNode[] =>
  collectSubtree(root).filter(s => isGradable(s.node));

/**
 * The top-level tasks that carry at least one gradable node in their subtree (see its docs) — the roots a
 * generation/refine call is addressed by (one LLM call per root subtree).
 */
export const solutionUnitRoots = (tasks: NestedExamTask[] | undefined): NestedExamTask[] =>
  (Array.isArray(tasks) ? tasks : []).filter(root => gradableSubtreeNodes(root).length > 0);

/**
 * The actual unit `SolutionProgress.total`/`startedForTasks`/`createdForTasks` count (see its doc comment):
 * every gradable node across every solution-unit root, not the roots themselves. A call is addressed to a
 * root, but rarely targets every node under it — counting nodes is what lets a refine of one subtask move
 * the bar by exactly that subtask instead of the whole main task it lives under.
 *
 * `step-utils.ts`'s `countSolutionUnitNodes` counts the SAME thing on the extraction side, against the
 * pre-structure-build tree shape (`taskRecords`) rather than this one — two implementations of one concept,
 * same as `solutionUnitRoots` / `isSolutionUnitRoot` already are for "root".
 */
export const solutionUnitNodeCount = (tasks: NestedExamTask[] | undefined): number =>
  solutionUnitRoots(tasks).reduce((sum, root) => sum + gradableSubtreeNodes(root).length, 0);

/** Gradable nodes in the subtree still missing a master solution. */
export const msTargets = (root: NestedExamTask): SubtreeNode[] =>
  gradableSubtreeNodes(root).filter(s => !hasMasterSolutionContent(s.node));

/** Gradable nodes in the subtree still missing a grading scheme. */
export const gsTargets = (root: NestedExamTask): SubtreeNode[] =>
  gradableSubtreeNodes(root).filter(s => !hasGradingSchemeContent(s.node));

/** GS may run once every gradable node in the subtree has master-solution content. */
export const subtreeMasterSolutionReady = (root: NestedExamTask): boolean => {
  const gradable = gradableSubtreeNodes(root);
  return gradable.length > 0 && gradable.every(s => hasMasterSolutionContent(s.node));
};
